import React, { useEffect, useState } from "react";
import {
  Box,
  Grid,
  Typography,
  Button,
  Paper,
  Avatar,
  Chip,
  Divider,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  CircularProgress,
  Select,
  MenuItem,
  FormControl,
} from "@mui/material";
import {
  AddCircleOutline,
  LocalShipping,
  ReportProblem,
  PersonAdd,
  AccountBalanceWallet,
  ShoppingCart,
  CheckCircle,
} from "@mui/icons-material";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from "recharts";
import { formatDistanceToNow } from "date-fns";
import axios from "../axiosInstance";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom"; 
import { useTheme, useMediaQuery } from "@mui/material"; 

export default function SellerDashboard() {
  const [days, setDays] = useState(7); 
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const { user } = useSelector((state) => state.user);

  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md")); 

  // --- Dynamic Greeting ---
  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return "Good Morning";
    if (hour < 17) return "Good Afternoon";
    return "Good Evening";
  };

  // --- API Fetch  ---
  const fetchDashboardData = async (filterDays) => {
    try {
      setLoading(true);
      const response = await axios.get(`/api/v1/dashboard/seller?days=${filterDays}`, {
        withCredentials: true,
      });
      if (response.data.success) {
        setData(response.data.data);
      }
    } catch (error) {
      console.error("Error fetching dashboard data:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboardData(days);
  }, [days]);

  if (loading && !data) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100vh" }}>
        <CircularProgress sx={{ color: "#0b4064" }} />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 4, bgcolor: "#f8fafc", minHeight: "100%", mb:5 }}>
      
      {/* Top Header with Filter Dropdown */}
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: 800, color: "#1e293b" }}>
          Seller Insights
        </Typography>
        
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <Select
            value={days}
            onChange={(e) => setDays(e.target.value)}
            displayEmpty
            sx={{ 
              borderRadius: 3, 
              bgcolor: "white", 
              boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
              fontWeight: 600,
              fontSize: "0.875rem"
            }}
          >
            <MenuItem value={7}>Last 7 Days</MenuItem>
            <MenuItem value={30}>Last 30 Days</MenuItem>
            <MenuItem value={90}>Last 3 months</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {/* Header Banner */}
      <Paper
        sx={{
          background: "linear-gradient(90deg, #0f172a 0%, #1e293b 100%)",
          borderRadius: 4,
          p: isMobile ? 3 : 4,
          mb: 4,
          display: "flex",
          flexDirection: isMobile ? "column" : "row", 
          justifyContent: "space-between",
          alignItems: isMobile ? "flex-start" : "center",
          gap: isMobile ? 3 : 0, 
          color: "white",
        }}
      >
        <Box>
          <Typography variant="body1" sx={{ opacity: 0.8, mb: 0.5 }}>
            {getGreeting()}, <b style={{color:"#fff"}}> {user?.name || "Seller"}</b> 
          </Typography>
          <Typography 
            variant={isMobile ? "h5" : "h4"} 
            sx={{ fontWeight: 700, lineHeight: 1.2 }}
          >
            Your business is <span style={{ color: "#22c55e" }}>up {data?.businessGrowth}%</span> this month.
          </Typography>
        </Box>

        <Button
          variant="contained"
          onClick={() => navigate("/seller-dashboard/catalog")} 
          startIcon={<AddCircleOutline />}
          fullWidth={isMobile} 
          sx={{
            bgcolor: "white",
            color: "#0b4064",
            fontWeight: 700,
            textTransform: "none",
            borderRadius: 3,
            px: 3,
            py: 1.2,
            boxShadow: "0 4px 14px rgba(0,0,0,0.1)",
            "&:hover": { bgcolor: "#f1f5f9" },
          }}
        >
          Add New Product
        </Button>
      </Paper>

      {/* Top Stats Row (Dynamic & Filtered) */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <StatCard 
          title="Total Receivables" 
          value={`₹${data?.totalReceivables?.toLocaleString()}`} 
          icon={<AccountBalanceWallet />} 
          color="#4f46e5" 
        />
        <StatCard 
          title="Orders in Transit" 
          value={data?.ordersInTransit} 
          badge={`${data?.ordersInTransit} Active`} 
          icon={<LocalShipping />} 
          color="#a855f7" 
        />
        <StatCard 
          title="Overdue Invoices" 
          value={data?.overdueInvoices} 
          actionText="View All" 
          icon={<ReportProblem />} 
          color="#f59e0b" 
          onClick={() => navigate("/seller-dashboard/pending-invoices")} 
        />
        <StatCard 
          title="Pending Requests" 
          value={data?.pendingRequests} 
          badge="New" 
          icon={<PersonAdd />} 
          color="#10b981" 
        />
      </Grid>

      <Grid container spacing={3}>
        {/* Revenue Performance (Last 6 Months) */}
        <Grid item xs={12} md={12} lg={8}>
          <Paper sx={{ p: 3, borderRadius: 4, height: "100%", boxShadow: "0 2px 10px rgba(0,0,0,0.02)" }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", mb: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>Revenue Performance</Typography>
              <Chip label="Monthly View" variant="outlined" size="small" />
            </Box>
            <Box sx={{ width: "100%", height: 320 }}>
              <ResponsiveContainer>
                <BarChart data={data?.graphData}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                  <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fill: "#64748b", fontSize: 12 }} />
                  <YAxis axisLine={false} tickLine={false} tick={{ fill: "#64748b", fontSize: 12 }} />
                  <Tooltip cursor={{ fill: "#f8fafc" }} />
                  <Bar dataKey="revenue" radius={[6, 6, 0, 0]}>
                    {data?.graphData.map((entry, index) => (
                      <Cell 
                        key={`cell-${index}`} 
                        fill={index === data.graphData.length - 1 ? "#4f46e5" : "#cbd5e1"} 
                      />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </Box>
          </Paper>
        </Grid>

        {/* Live Feed (Orders & Payments) */}
        <Grid item xs={12} md={12} lg={4} sx={{
          mt: {xs: 6, sm:6, md: 6, lg: 0,}
        }}>
          <Paper sx={{ p: 3, borderRadius: 4, height: "100%", boxShadow: "0 2px 10px rgba(0,0,0,0.02)" }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>Live Feed</Typography>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                 <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: "#22c55e", mr: 1 }} />
                 <Typography variant="caption" color="textSecondary">Live</Typography>
              </Box>
            </Box>
            <List disablePadding>
              {data?.liveFeed?.length > 0 ? (
                data?.liveFeed?.map((item, index) => (
                  <React.Fragment key={item.id}>
                    <FeedItem 
                      name={item.name} 
                      action={item.action} 
                      time={formatDistanceToNow(new Date(item.time), { addSuffix: true })} 
                      amount={item.amount} 
                      icon={item.type === "order" ? <ShoppingCart /> : <CheckCircle />} 
                      iconBg={item.type === "order" ? "#eff6ff" : "#f0fdf4"} 
                      iconColor={item.type === "order" ? "#0b4064" : "#22c55e"} 
                    />
                    {index < data.liveFeed.length - 1 && <Divider variant="inset" component="li" sx={{ my: 1, opacity: 0.5 }} />}
                  </React.Fragment>
                ))
              ) : (
                <Typography variant="body2" color="textSecondary" textAlign="center" sx={{ py: 4 }}>
                  No recent activity found.
                </Typography>
              )}
            </List>

          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
}

// --- Sub-Components ---
function StatCard({ title, value, badge, actionText, icon, color, onClick }) {
  return (
    <Grid item xs={12} sm={12} md={6} lg={3}>
      <Paper 
        onClick={onClick}
        sx={{ 
          p: 3, 
          borderRadius: 4, 
          border: "1px solid #f1f5f9", 
          boxShadow: "0 4px 12px rgba(0,0,0,0.03)",
          cursor: onClick ? "pointer" : "default", 
          transition: "transform 0.2s",
          "&:hover": onClick ? { transform: "translateY(-4px)", boxShadow: "0 6px 15px rgba(0,0,0,0.08)" } : {}
        }}
      >
        <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
          <Avatar sx={{ bgcolor: `${color}15`, color: color, borderRadius: 2.5, width: 45, height: 45 }}>
            {icon}
          </Avatar>
          {badge && <Chip label={badge} size="small" sx={{ bgcolor: "#f1f5f9", color: "#64748b", fontWeight: 700, fontSize: 10 }} />}
          
          {actionText && (
            <Typography 
              variant="caption" 
              onClick={(e) => {
                e.stopPropagation(); 
                if(onClick) onClick();
              }}
              sx={{ 
                color: "#0b4064", 
                fontWeight: 700, 
                cursor: "pointer", 
                "&:hover": { textDecoration: 'underline' } 
              }}
            >
              {actionText}
            </Typography>
          )}
        </Box>
        <Typography variant="body2" sx={{ color: "#64748b", fontWeight: 600 }}>{title}</Typography>
        <Typography variant="h5" sx={{ fontWeight: 800, mt: 0.5, color: "#0f172a" }}>{value}</Typography>
      </Paper>
    </Grid>
  );
}

function FeedItem({ name, action, time, amount, icon, iconBg, iconColor }) {
  return (
    <ListItem alignItems="flex-start" sx={{ px: 0 }}>
      <ListItemAvatar>
        <Avatar
          sx={{
            bgcolor: iconBg,
            color: iconColor,
            width: 40,
            height: 40,
          }}
        >
          {icon}
        </Avatar>
      </ListItemAvatar>

      <Box sx={{ width: "100%" }}>
        {/* Top Row */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            flexDirection: { xs: "column", sm: "row" }, 
            gap: { xs: 0.5, sm: 0 },
          }}
        >
          <Typography
            variant="subtitle2"
            sx={{ fontWeight: 700, color: "#1e293b" }}
          >
            {name}
          </Typography>

          {amount && (
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: 800,
                color: amount.includes("+") ? "#22c55e" : "#1e293b",
              }}
            >
              {amount}
            </Typography>
          )}
        </Box>

        {/* Bottom Row */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            flexDirection: { xs: "column", sm: "row" }, 
            gap: { xs: 0.5, sm: 0 },
            mt: 0.3,
          }}
        >
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ fontWeight: 500 }}
          >
            {action}
          </Typography>

          <Typography variant="caption" color="text.secondary">
            {time}
          </Typography>
        </Box>
      </Box>
    </ListItem>
  );
}
